import { schemaMetadataFileName } from "@gadgetinc/conventions";
import { comparer, computed, makeObservable } from "mobx";
import {
  fileIsGlobalAction,
  fileIsGlobalView,
  fileIsNamespacedModelSchemaMetadata,
  fileIsNonNamespacedModelSchemaMetadata,
} from "./file-based-actions/filePathUtils";
import type { ModelIndexEntry, SourceFileIndexEntry } from "./IndexEntry";
import { memoize } from "./Memoizable";

export class FileSetCache {
  memos: Record<string, any> = {};

  constructor(
    readonly files: () => SourceFileIndexEntry[],
    readonly canHaveNamespacedModels: () => boolean,
    readonly memoize: boolean,
    readonly modelsWithoutProblemsData: () => ModelIndexEntry[]
  ) {
    makeObservable(this);
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get allPaths() {
    return this.files().map((file) => file.path);
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get schemaFilePaths() {
    return Array.from(
      new Set([...this.schemaFiles.map((file) => file.path), ...this.modelsWithoutProblemsData().map((model) => model.schemaFilePath)])
    );
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get modelActionFilePaths() {
    const allModelActionFolders = this.schemaFilePaths.map((schemaFilePath) =>
      schemaFilePath.replace(`/${schemaMetadataFileName}`, "/actions")
    );

    return this.files()
      .filter((file) => file.isExecutableJsOrTsFile && allModelActionFolders.includes(file.dirname))
      .map((file) => file.path);
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get globalActionFiles() {
    return this.files().filter((file) => fileIsGlobalAction(file, this.canHaveNamespacedModels()));
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get globalViewFiles() {
    return this.files().filter((file) => fileIsGlobalView(file, this.canHaveNamespacedModels()));
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get globalActionFilePaths() {
    return this.globalActionFiles.map((file) => file.path);
  }

  @memoize
  @computed({ equals: comparer.shallow })
  get allActionFilePaths() {
    return [...this.globalActionFilePaths, ...this.modelActionFilePaths];
  }

  @memoize
  @computed({ equals: comparer.shallow })
  private get schemaFiles() {
    return this.files().filter((file) =>
      this.canHaveNamespacedModels() ? fileIsNamespacedModelSchemaMetadata(file) : fileIsNonNamespacedModelSchemaMetadata(file)
    );
  }
}
